import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { useDesignSystem } from './ThemeProvider';

const DEFAULT_UNSAVED_MESSAGE = 'You have unsaved changes. Leave without saving?';

const FormSafetyContext = createContext(null);

function getActiveGuard(guards) {
  return Array.from(guards.values()).find((guard) => guard?.when);
}

export function useUnsavedChangesGuard({ when, message = DEFAULT_UNSAVED_MESSAGE }) {
  const context = useContext(FormSafetyContext);
  const guardIdRef = useRef(`form-guard-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!context) {
      return undefined;
    }

    const guardId = guardIdRef.current;
    context.setGuard(guardId, { when, message });

    return () => {
      context.clearGuard(guardId);
    };
  }, [context, message, when]);
}

export function useGuardedNavigate() {
  const navigate = useNavigate();
  const context = useContext(FormSafetyContext);

  return useCallback(
    (to, options) => {
      if (!context) {
        navigate(to, options);
        return true;
      }

      const navigationState = context.confirmNavigation(() => {
        navigate(to, options);
      });

      if (navigationState === 'allow') {
        navigate(to, options);
        return true;
      }

      if (navigationState === 'pending') {
        return false;
      }

      if (navigationState === 'reject') {
        return false;
      }

      return true;
    },
    [context, navigate]
  );
}

export default function FormSafetyProvider({ children }) {
  const { uiCustomization } = useDesignSystem();
  const [, setGuardVersion] = useState(0);
  const [leavePrompt, setLeavePrompt] = useState(null);
  const guardsRef = useRef(new Map());
  const pendingNavigationRef = useRef(null);

  const setGuard = useCallback((guardId, nextValue) => {
    const currentValue = guardsRef.current.get(guardId);

    if (
      currentValue &&
      currentValue.when === nextValue.when &&
      currentValue.message === nextValue.message
    ) {
      return;
    }

    guardsRef.current.set(guardId, nextValue);
    setGuardVersion((currentValueCount) => currentValueCount + 1);
  }, []);

  const clearGuard = useCallback((guardId) => {
    if (!guardsRef.current.has(guardId)) {
      return;
    }

    guardsRef.current.delete(guardId);
    setGuardVersion((currentValueCount) => currentValueCount + 1);
  }, []);

  const confirmNavigation = useCallback((onConfirm) => {
    const activeGuard = getActiveGuard(guardsRef.current);

    if (!activeGuard || !uiCustomization.showUnsavedAlerts) {
      return 'allow';
    }

    const message = activeGuard.message || DEFAULT_UNSAVED_MESSAGE;

    if (uiCustomization.showBrowserUnsavedAlerts) {
      return window.confirm(message) ? 'allow' : 'reject';
    }

    pendingNavigationRef.current = typeof onConfirm === 'function' ? onConfirm : null;
    setLeavePrompt({
      message,
    });
    return 'pending';
  }, [uiCustomization.showBrowserUnsavedAlerts, uiCustomization.showUnsavedAlerts]);

  const activeGuard = getActiveGuard(guardsRef.current);

  useEffect(() => {
    if (!leavePrompt) {
      return;
    }

    if (!activeGuard || !uiCustomization.showUnsavedAlerts || uiCustomization.showBrowserUnsavedAlerts) {
      setLeavePrompt(null);
    }
  }, [activeGuard, leavePrompt, uiCustomization.showBrowserUnsavedAlerts, uiCustomization.showUnsavedAlerts]);

  useEffect(() => {
    if (
      !activeGuard ||
      !uiCustomization.showUnsavedAlerts ||
      !uiCustomization.showBrowserUnsavedAlerts
    ) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = activeGuard.message || DEFAULT_UNSAVED_MESSAGE;
      return activeGuard.message || DEFAULT_UNSAVED_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeGuard, uiCustomization.showBrowserUnsavedAlerts, uiCustomization.showUnsavedAlerts]);

  const cancelLeavePrompt = useCallback(() => {
    pendingNavigationRef.current = null;
    setLeavePrompt(null);
  }, []);

  const confirmLeavePrompt = useCallback(() => {
    const pendingNavigation = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setLeavePrompt(null);
    pendingNavigation?.();
  }, []);

  const contextValue = useMemo(
    () => ({
      clearGuard,
      confirmNavigation,
      setGuard,
    }),
    [clearGuard, confirmNavigation, setGuard]
  );

  return (
    <FormSafetyContext.Provider value={contextValue}>
      {children}
      <Modal
        open={Boolean(leavePrompt)}
        title="Leave without saving?"
        okText="Leave page"
        cancelText="Stay on page"
        onOk={confirmLeavePrompt}
        onCancel={cancelLeavePrompt}
        destroyOnHidden
        maskClosable={false}
      >
        <p>{leavePrompt?.message || DEFAULT_UNSAVED_MESSAGE}</p>
      </Modal>
    </FormSafetyContext.Provider>
  );
}
